import { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  doc
} from 'firebase/firestore';
import {
  Banknote,
  CheckCircle2,
  Clock3,
  Coins,
  History,
  MinusCircle,
  Plus,
  ReceiptText,
  ShieldCheck,
  Trash2
} from 'lucide-react';
import { useAuth } from '../../core/auth/AuthProvider';
import { db } from '../../core/firebase/firebase';

const money = (value) => `${Number(value || 0).toFixed(0)} DT`;
const text = (value) => String(value ?? '').trim();
const todayIso = () => new Date().toISOString().slice(0, 10);

const timestampValue = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (value?.seconds) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDateTime = (value) => {
  const millis = timestampValue(value);
  if (!millis) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(millis));
};

const paymentParts = (payment) => {
  if (Array.isArray(payment?.paymentParts) && payment.paymentParts.length) {
    return payment.paymentParts;
  }

  return [{
    amount: Number(payment?.amount || 0),
    paymentDate: payment?.paymentDate || '',
    paymentMethod: payment?.paymentMethod || 'other',
    reference: payment?.reference || '',
    createdByUid: payment?.createdByUid || null,
    createdByName: payment?.createdByName || ''
  }];
};

function cashPartsFromPayments(payments) {
  return payments.flatMap((payment) =>
    paymentParts(payment).map((part, index) => ({
      key: `${payment.id}:${index}`,
      paymentId: payment.id,
      receiptNumber: payment.receiptNumber || '—',
      parentName: payment.parentName || '—',
      studentName: payment.studentName || '',
      amount: Number(part.amount || 0),
      paymentDate: part.paymentDate || payment.paymentDate || '',
      paymentMethod: part.paymentMethod || payment.paymentMethod || 'other',
      reference: part.reference || '',
      agentUid: part.createdByUid || payment.createdByUid || null,
      agentName: part.createdByName || payment.createdByName || 'Agent administratif',
      paymentStatus: payment.status || 'confirmed'
    }))
  ).filter((part) => part.paymentMethod === 'cash' && part.paymentStatus !== 'cancelled');
}

const closureStatusLabel = (status) => ({
  submitted: 'En attente du gérant',
  confirmed: 'Remise confirmée',
  rejected: 'À corriger'
})[status] || status;

export default function CashPage() {
  const { profile, isGerant } = useAuth();
  const [payments, setPayments] = useState([]);
  const [closures, setClosures] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseLabel, setExpenseLabel] = useState('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [declaredAmount, setDeclaredAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const offPayments = onSnapshot(collection(db, 'payments'), (snapshot) => {
      setPayments(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    });

    const closuresSource = isGerant
      ? collection(db, 'cashClosures')
      : query(collection(db, 'cashClosures'), where('agentUid', '==', profile?.id || ''));

    const offClosures = onSnapshot(closuresSource, (snapshot) => {
      setClosures(
        snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => timestampValue(b.submittedAt) - timestampValue(a.submittedAt))
      );
    });

    return () => {
      offPayments();
      offClosures();
    };
  }, [isGerant, profile?.id]);

  const cashParts = useMemo(() => cashPartsFromPayments(payments), [payments]);
  const alreadyClosedKeys = useMemo(() => {
    const keys = new Set();
    closures
      .filter((closure) => ['submitted', 'confirmed'].includes(closure.status))
      .forEach((closure) => {
        (closure.paymentPartKeys || []).forEach((key) => keys.add(key));
      });
    return keys;
  }, [closures]);

  const openCashParts = useMemo(
    () => cashParts.filter((part) => !alreadyClosedKeys.has(part.key)),
    [cashParts, alreadyClosedKeys]
  );

  const ownUid = profile?.id || '';
  const ownCashParts = useMemo(
    () => openCashParts.filter((part) => part.agentUid === ownUid),
    [openCashParts, ownUid]
  );

  const ownPendingClosure = useMemo(
    () => closures.find((closure) => closure.agentUid === ownUid && closure.status === 'submitted'),
    [closures, ownUid]
  );

  const ownCashTotal = ownCashParts.reduce((sum, part) => sum + part.amount, 0);
  const expenseTotal = expenses.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const expectedAmount = Math.max(0, ownCashTotal - expenseTotal);
  const declared = declaredAmount === '' ? null : Number(declaredAmount);
  const difference = declared == null || !Number.isFinite(declared)
    ? null
    : declared - expectedAmount;

  const pendingClosures = closures.filter((closure) => closure.status === 'submitted');
  const confirmedClosures = closures.filter((closure) => closure.status === 'confirmed');

  const managerOpenByAgent = useMemo(() => {
    const groups = new Map();
    openCashParts.forEach((part) => {
      if (!part.agentUid) return;
      const current = groups.get(part.agentUid) || {
        agentUid: part.agentUid,
        agentName: part.agentName,
        total: 0,
        count: 0
      };
      current.total += part.amount;
      current.count += 1;
      groups.set(part.agentUid, current);
    });
    return [...groups.values()].sort((a, b) => b.total - a.total);
  }, [openCashParts]);

  function addExpense() {
    const amount = Number(expenseAmount);
    if (!text(expenseLabel)) {
      setError('Indiquez le motif de la sortie de caisse.');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Le montant de la sortie doit être supérieur à zéro.');
      return;
    }
    if (expenseTotal + amount > ownCashTotal) {
      setError('Les sorties ne peuvent pas dépasser les espèces encaissées.');
      return;
    }

    setExpenses((current) => [
      ...current,
      {
        id: `${Date.now()}-${current.length}`,
        label: text(expenseLabel),
        amount
      }
    ]);
    setExpenseLabel('');
    setExpenseAmount('');
    setError('');
  }

  async function submitClosure() {
    if (ownPendingClosure) {
      setError('Une remise de caisse est déjà en attente de confirmation du gérant.');
      return;
    }
    if (!ownCashParts.length) {
      setError('Aucun encaissement en espèces à remettre.');
      return;
    }
    if (declared == null || !Number.isFinite(declared) || declared < 0) {
      setError('Indiquez le montant réellement remis au gérant.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await addDoc(collection(db, 'cashClosures'), {
        agentUid: ownUid,
        agentName: text(profile?.fullName || profile?.name) || 'Agent administratif',
        cashTotal: ownCashTotal,
        paymentCount: ownCashParts.length,
        paymentPartKeys: ownCashParts.map((part) => part.key),
        paymentParts: ownCashParts.map((part) => ({
          key: part.key,
          paymentId: part.paymentId,
          receiptNumber: part.receiptNumber,
          parentName: part.parentName,
          studentName: part.studentName,
          amount: part.amount,
          paymentDate: part.paymentDate
        })),
        expenses: expenses.map(({ label, amount }) => ({ label, amount })),
        expensesTotal: expenseTotal,
        expectedAmount,
        declaredAmount: declared,
        difference: declared - expectedAmount,
        status: 'submitted',
        closureDate: todayIso(),
        submittedAt: serverTimestamp(),
        confirmedAt: null,
        confirmedByUid: null,
        confirmedByName: null
      });

      setExpenses([]);
      setDeclaredAmount('');
      setMessage('Vide de caisse soumis au gérant.');
      window.setTimeout(() => setMessage(''), 3500);
    } catch (err) {
      console.error(err);
      setError(`Soumission impossible : ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function confirmClosure(closure) {
    setSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, 'cashClosures', closure.id), {
        status: 'confirmed',
        confirmedAt: serverTimestamp(),
        confirmedByUid: ownUid,
        confirmedByName: text(profile?.fullName || profile?.name) || 'Gérant'
      });
      setMessage(`Remise de ${closure.agentName || 'l’agent'} confirmée.`);
      window.setTimeout(() => setMessage(''), 3500);
    } catch (err) {
      console.error(err);
      setError(`Confirmation impossible : ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  if (isGerant) {
    return (
      <div className="cash-page">
        {message && <div className="success-note cash-feedback"><CheckCircle2 size={18} />{message}</div>}
        {error && <div className="alert alert-error cash-feedback">{error}</div>}

        <section className="cash-kpis">
          <article>
            <Clock3 />
            <div><span>Remises en attente</span><strong>{pendingClosures.length}</strong></div>
          </article>
          <article>
            <Banknote />
            <div><span>Espèces non clôturées</span><strong>{money(openCashParts.reduce((sum, item) => sum + item.amount, 0))}</strong></div>
          </article>
          <article>
            <ShieldCheck />
            <div><span>Remises confirmées</span><strong>{confirmedClosures.length}</strong></div>
          </article>
        </section>

        <section className="panel cash-section">
          <div className="panel-heading cash-section-heading">
            <div>
              <p className="eyebrow">À CONTRÔLER</p>
              <h2>Remises en attente</h2>
            </div>
          </div>

          {pendingClosures.length ? (
            <div className="cash-closure-list">
              {pendingClosures.map((closure) => (
                <article className="cash-closure-card" key={closure.id}>
                  <div className="cash-closure-main">
                    <div>
                      <span className="cash-status cash-status-pending">En attente</span>
                      <h3>{closure.agentName || 'Agent administratif'}</h3>
                      <small>Soumis le {formatDateTime(closure.submittedAt)}</small>
                    </div>
                    <div className="cash-closure-amount">
                      <span>Montant déclaré</span>
                      <strong>{money(closure.declaredAmount)}</strong>
                    </div>
                  </div>

                  <div className="cash-breakdown">
                    <div><span>Espèces encaissées</span><strong>{money(closure.cashTotal)}</strong></div>
                    <div><span>Sorties</span><strong>- {money(closure.expensesTotal)}</strong></div>
                    <div><span>Théorique</span><strong>{money(closure.expectedAmount)}</strong></div>
                    <div className={Number(closure.difference || 0) === 0 ? 'cash-ok' : 'cash-warning'}>
                      <span>Écart</span><strong>{money(closure.difference)}</strong>
                    </div>
                  </div>

                  {(closure.expenses || []).length > 0 && (
                    <div className="cash-expense-summary">
                      <strong>Sorties déclarées</strong>
                      {(closure.expenses || []).map((expense, index) => (
                        <span key={`${closure.id}-expense-${index}`}>{expense.label} · {money(expense.amount)}</span>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    className="button button-primary cash-confirm-button"
                    disabled={saving}
                    onClick={() => confirmClosure(closure)}
                  >
                    <ShieldCheck size={17} />
                    Confirmer la réception
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <div className="compact-empty">Aucune remise en attente de confirmation.</div>
          )}
        </section>

        <section className="panel cash-section">
          <div className="panel-heading cash-section-heading">
            <div>
              <p className="eyebrow">EN COURS</p>
              <h2>Caisse actuelle des agents</h2>
            </div>
          </div>
          {managerOpenByAgent.length ? (
            <div className="cash-agent-grid">
              {managerOpenByAgent.map((agent) => (
                <article key={agent.agentUid}>
                  <Coins size={20} />
                  <div><strong>{agent.agentName}</strong><span>{agent.count} encaissement(s) en espèces</span></div>
                  <b>{money(agent.total)}</b>
                </article>
              ))}
            </div>
          ) : (
            <div className="compact-empty">Aucune espèce non clôturée actuellement.</div>
          )}
        </section>

        <section className="panel cash-section">
          <div className="panel-heading cash-section-heading">
            <div>
              <p className="eyebrow">HISTORIQUE</p>
              <h2>Remises confirmées</h2>
            </div>
          </div>
          {confirmedClosures.length ? (
            <div className="cash-history-list">
              {confirmedClosures.slice(0, 30).map((closure) => (
                <article key={closure.id}>
                  <History size={18} />
                  <div>
                    <strong>{closure.agentName || 'Agent administratif'}</strong>
                    <span>{formatDateTime(closure.confirmedAt)} · {closure.paymentCount || 0} encaissement(s)</span>
                  </div>
                  <div className="cash-history-amount">
                    <strong>{money(closure.declaredAmount)}</strong>
                    <span className={Number(closure.difference || 0) === 0 ? 'cash-ok' : 'cash-warning'}>
                      Écart {money(closure.difference)}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="compact-empty">Aucune remise confirmée pour le moment.</div>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className="cash-page">
      {message && <div className="success-note cash-feedback"><CheckCircle2 size={18} />{message}</div>}
      {error && <div className="alert alert-error cash-feedback">{error}</div>}

      {ownPendingClosure && (
        <div className="cash-pending-banner">
          <Clock3 size={20} />
          <div>
            <strong>Une remise est en attente de confirmation</strong>
            <span>{money(ownPendingClosure.declaredAmount)} remis · soumis le {formatDateTime(ownPendingClosure.submittedAt)}</span>
          </div>
        </div>
      )}

      <section className="cash-kpis">
        <article>
          <Banknote />
          <div><span>Espèces à clôturer</span><strong>{money(ownCashTotal)}</strong></div>
        </article>
        <article>
          <ReceiptText />
          <div><span>Encaissements</span><strong>{ownCashParts.length}</strong></div>
        </article>
        <article>
          <MinusCircle />
          <div><span>Sorties saisies</span><strong>{money(expenseTotal)}</strong></div>
        </article>
        <article>
          <Coins />
          <div><span>Théorique à remettre</span><strong>{money(expectedAmount)}</strong></div>
        </article>
      </section>

      <div className="cash-two-columns">
        <section className="panel cash-section">
          <div className="panel-heading cash-section-heading">
            <div>
              <p className="eyebrow">MA CAISSE</p>
              <h2>Encaissements en espèces</h2>
            </div>
          </div>

          {ownCashParts.length ? (
            <div className="cash-payment-list">
              {ownCashParts.map((part) => (
                <article key={part.key}>
                  <ReceiptText size={17} />
                  <div>
                    <strong>{part.receiptNumber}</strong>
                    <span>{part.parentName}{part.studentName ? ` · ${part.studentName}` : ''}</span>
                  </div>
                  <div>
                    <strong>{money(part.amount)}</strong>
                    <span>{part.paymentDate || '—'}</span>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="compact-empty">Aucun encaissement en espèces non clôturé.</div>
          )}
        </section>

        <section className="panel cash-section">
          <div className="panel-heading cash-section-heading">
            <div>
              <p className="eyebrow">CLÔTURE</p>
              <h2>Préparer le vide de caisse</h2>
            </div>
          </div>

          <div className="cash-expense-form">
            <label>
              Motif d’une sortie de caisse
              <input
                value={expenseLabel}
                onChange={(event) => setExpenseLabel(event.target.value)}
                placeholder="Ex. achat urgent de fournitures"
                disabled={Boolean(ownPendingClosure)}
              />
            </label>
            <label>
              Montant
              <input
                type="number"
                min="0"
                step="0.001"
                value={expenseAmount}
                onChange={(event) => setExpenseAmount(event.target.value)}
                placeholder="0"
                disabled={Boolean(ownPendingClosure)}
              />
            </label>
            <button type="button" className="button button-secondary" onClick={addExpense} disabled={Boolean(ownPendingClosure)}>
              <Plus size={16} /> Ajouter la sortie
            </button>
          </div>

          {expenses.length > 0 && (
            <div className="cash-expenses-list">
              {expenses.map((expense) => (
                <div key={expense.id}>
                  <span>{expense.label}</span>
                  <strong>{money(expense.amount)}</strong>
                  <button type="button" onClick={() => setExpenses((current) => current.filter((item) => item.id !== expense.id))} aria-label="Supprimer la sortie">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="cash-total-box">
            <div><span>Espèces encaissées</span><strong>{money(ownCashTotal)}</strong></div>
            <div><span>Sorties</span><strong>- {money(expenseTotal)}</strong></div>
            <div className="cash-total-highlight"><span>Montant théorique</span><strong>{money(expectedAmount)}</strong></div>
          </div>

          <label className="cash-declared-input">
            Montant réellement remis au gérant
            <input
              type="number"
              min="0"
              step="0.001"
              value={declaredAmount}
              onChange={(event) => setDeclaredAmount(event.target.value)}
              placeholder={String(expectedAmount || 0)}
              disabled={Boolean(ownPendingClosure)}
            />
          </label>

          {difference != null && (
            <div className={`cash-difference ${difference === 0 ? 'cash-difference-ok' : 'cash-difference-warning'}`}>
              <span>Écart constaté</span>
              <strong>{money(difference)}</strong>
            </div>
          )}

          <button
            type="button"
            className="button button-primary cash-submit-button"
            onClick={submitClosure}
            disabled={saving || Boolean(ownPendingClosure) || !ownCashParts.length}
          >
            <Banknote size={17} />
            {ownPendingClosure ? 'En attente de confirmation' : 'Soumettre le vide de caisse au gérant'}
          </button>
        </section>
      </div>

      <section className="panel cash-section">
        <div className="panel-heading cash-section-heading">
          <div>
            <p className="eyebrow">HISTORIQUE</p>
            <h2>Mes remises de caisse</h2>
          </div>
        </div>
        {closures.filter((closure) => closure.agentUid === ownUid).length ? (
          <div className="cash-history-list">
            {closures.filter((closure) => closure.agentUid === ownUid).slice(0, 20).map((closure) => (
              <article key={closure.id}>
                <History size={18} />
                <div>
                  <strong>{closureStatusLabel(closure.status)}</strong>
                  <span>{closure.closureDate || '—'} · {closure.paymentCount || 0} encaissement(s)</span>
                </div>
                <div className="cash-history-amount">
                  <strong>{money(closure.declaredAmount)}</strong>
                  <span className={Number(closure.difference || 0) === 0 ? 'cash-ok' : 'cash-warning'}>Écart {money(closure.difference)}</span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="compact-empty">Aucune remise de caisse enregistrée.</div>
        )}
      </section>
    </div>
  );
}
